/**
 * Görüntüleme modu → analiz katmanı görünürlük çözümleyicisi.
 * Tek mekanizma: Detail / Coverage / Administrative.
 * Hedef modda veri yoksa Detail'e düşer (zoom-out boş harita önlenir).
 */
import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAnalysisWorkspaceStore } from '@/stores/analysisWorkspace.store'
import { useMapLayerStore } from '@/stores/mapLayer.store'
import { LAYER_CATEGORIES } from '@/composables/gtfs/map/gtfsMapLayers.js'

const OWNER_MODE_LAYERS = {
  isochrone: {
    detail: ['detail'],
    coverage: ['coverage'],
    administrative: ['administrative'],
  },
  catchment: {
    detail: ['detail'],
    coverage: ['coverage'],
    administrative: ['administrative'],
  },
  corridor: {
    detail: ['detail'],
  },
  'service-availability': {
    // Buffer görünümü (durak + yürüme buffer)
    detail: ['detail-buffers', 'detail-stops'],
    // Mahalle choropleth
    coverage: ['administrative'],
    // İlçe choropleth
    administrative: ['ilce-choropleth'],
  },
  'transit-desert': {
    administrative: ['administrative'],
  },
}

function layerHasData(layerStore, ownerId, layerId) {
  const rec = layerStore.getLayer(
    LAYER_CATEGORIES.analysis,
    ownerId,
    layerId
  )
  return !!rec?.meta?.hasData
}

function resolveShowIds(ownerId, mode, layerStore) {
  const mapping = OWNER_MODE_LAYERS[ownerId]
  if (!mapping) return new Set()

  let showIds = [...(mapping[mode] || [])]

  // Coverage/Admin boşsa Detail halkalarını tut (zoom otomatik geçiş flicker)
  // SA'da görünümler ayrı — karıştırmamak için fallback yok
  if (
    ownerId !== 'service-availability' &&
    (mode === 'coverage' || mode === 'administrative')
  ) {
    const anyData = showIds.some((id) => layerHasData(layerStore, ownerId, id))
    if (!anyData && mapping.detail?.length) {
      showIds = [...mapping.detail]
    }
  }

  return new Set(showIds)
}

export function applyViewModeVisibility(ownerId, mode) {
  if (!ownerId) return
  const layerStore = useMapLayerStore()
  const mapping = OWNER_MODE_LAYERS[ownerId]
  if (!mapping) return

  const ownerLayers = layerStore
    .byOwner(ownerId)
    .filter((l) => l.category === LAYER_CATEGORIES.analysis)

  const showIds = resolveShowIds(ownerId, mode, layerStore)
  for (const rec of ownerLayers) {
    const shouldShow = showIds.has(rec.layerId)
    rec.visible = shouldShow
    layerStore._syncVisibility(rec)
  }
}

/**
 * Workspace effectiveViewMode değişince görünürlüğü güncelle.
 */
export function useAnalysisViewModeSync() {
  const workspace = useAnalysisWorkspaceStore()
  const { effectiveViewMode, activeOwnerId, mapZoom } = storeToRefs(workspace)

  watch(
    [effectiveViewMode, activeOwnerId],
    ([mode, owner]) => {
      applyViewModeVisibility(owner, mode)
    },
    { immediate: true }
  )

  return {
    applyViewModeVisibility,
    setMapZoom: (z) => workspace.setMapZoom(z),
    mapZoom,
  }
}
